import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Dialog from "../../../components/ui/Dialog";
import Pagination from "../../../components/ui/Pagination";
import BranchSelect from "../../../components/ui/BranchSelect";
import {
  useClientVisits,
  useReviewClientVisit,
} from "../../../api/queries/workflowQueries";
import useCurrentUser from "../../../hooks/useCurrentUser";
import { useCanModify } from "../../../hooks/usePermissions";
import { getApiErrorMessage, getApiErrorStatus } from "../../../utils/apiError";
import {
  formatDateTime,
  formatPlainDate,
  fromNow,
} from "../../../utils/dateTime";
import ApprovalTrail from "./ApprovalTrail";

const EMPTY = [];
const STATUSES = [
  { value: "PENDING", label: "Pending", tone: "amber" },
  { value: "APPROVED", label: "Approved", tone: "green" },
  { value: "REJECTED", label: "Rejected", tone: "red" },
  { value: "CANCELLED", label: "Cancelled", tone: "gray" },
];
const NO_LOCATION =
  "No saved location: check-ins there can only be flagged for review.";

const ReviewDialog = ({ visit, action, onClose }) => {
  const isReject = action === "REJECT";
  const [remarks, setRemarks] = useState("");
  const review = useReviewClientVisit();
  const noLocation = (visit.clients || []).filter((c) => !c.hasLocation);

  const submit = () =>
    review.mutate(
      { id: visit.id, action, remarks: remarks.trim() || undefined },
      {
        onSuccess: () => {
          toast.success(
            isReject ? "Client visit rejected." : "Client visit approved.",
          );
          onClose();
        },
        onError: (err) => {
          // 403 is already shown by the global handler
          if (getApiErrorStatus(err) !== 403)
            toast.error(getApiErrorMessage(err, "Could not save the review."));
          // 409: decided by someone else, or the date has arrived
          if (getApiErrorStatus(err) === 409) onClose();
        },
      },
    );

  return (
    <Dialog
      title={isReject ? "Reject client visit?" : "Approve client visit?"}
      subtitle={`${visit.employee?.name || ""} · ${formatPlainDate(visit.date)}`}
      onClose={onClose}
      busy={review.isPending}
      footer={
        <>
          <button
            type="button"
            className="btn btn-outline-secondary profile-btn"
            onClick={onClose}
            disabled={review.isPending}
          >
            Close
          </button>
          <button
            type="button"
            className={`btn profile-btn ${isReject ? "btn-danger" : "btn-success"}`}
            onClick={submit}
            disabled={review.isPending || (isReject && !remarks.trim())}
          >
            {review.isPending && (
              <span
                className="spinner-border spinner-border-sm"
                aria-hidden="true"
              ></span>
            )}
            {isReject ? "Reject" : "Approve"}
          </button>
        </>
      }
    >
      <p className="fs-085 mb-2">
        <strong>
          {(visit.clients || []).map((c) => c.name).join(", ") || "—"}
        </strong>
      </p>
      <div className="approval-note mb-3">{visit.purpose || "—"}</div>
      {!isReject && noLocation.length > 0 && (
        <div className="alert alert-warning fs-080 py-2" role="alert">
          {noLocation.map((c) => c.name).join(", ")}: {NO_LOCATION}
        </div>
      )}
      <label htmlFor="visit-remarks" className="profile-label">
        {isReject ? "Reason" : "Remarks"}{" "}
        {isReject ? (
          <span className="text-danger">*</span>
        ) : (
          <span className="text-slate-400 fw-normal">(optional)</span>
        )}
      </label>
      <textarea
        id="visit-remarks"
        className="form-control"
        rows={3}
        maxLength={500}
        value={remarks}
        onChange={(e) => setRemarks(e.target.value)}
        autoFocus
      />
    </Dialog>
  );
};

/** Client visits filed ahead of the day. One approval decides, before the visit date. */
const ClientVisitQueue = () => {
  const user = useCurrentUser();
  const canModify = useCanModify("attendance");
  const [status, setStatus] = useState("PENDING");
  const [branchId, setBranchId] = useState("");
  const [assignmentType, setAssignmentType] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [reviewing, setReviewing] = useState(null); // { visit, action }

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchText.trim()), 300);
    return () => clearTimeout(t);
  }, [searchText]);
  useEffect(
    () => setPage(1),
    [status, branchId, assignmentType, from, to, search],
  );

  const query = useClientVisits({
    status,
    from,
    to,
    companyId: user.companyId || undefined,
    branchId,
    assignmentType,
    search,
    page,
    limit,
  });
  const rows = query.data?.data || EMPTY;

  return (
    <>
      <div className="att-filters mb-3">
        <select
          className="status-select"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          aria-label="Status"
        >
          {STATUSES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        <div className="position-relative att-filter-search">
          <i className="bi bi-search position-absolute top-50 translate-middle-y ms-3 text-slate-500 fs-080"></i>
          <input
            type="search"
            className="filter-input w-100 ps-5"
            placeholder="Name or employee no."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            aria-label="Search employees"
          />
        </div>
        <div className="att-filter-branch">
          <BranchSelect
            value={branchId}
            onChange={(v) => setBranchId(v || "")}
            id="visit-branch"
          />
        </div>
        <select
          className="status-select"
          value={assignmentType}
          onChange={(e) => setAssignmentType(e.target.value)}
          aria-label="Employee type"
        >
          <option value="">Internal and deployed</option>
          <option value="Internal">Internal</option>
          <option value="Deployed">Deployed</option>
        </select>
        <input
          type="date"
          className="filter-input"
          value={from}
          max={to || undefined}
          onChange={(e) => setFrom(e.target.value)}
          aria-label="From date"
        />
        <span className="text-slate-400">–</span>
        <input
          type="date"
          className="filter-input"
          value={to}
          min={from || undefined}
          onChange={(e) => setTo(e.target.value)}
          aria-label="To date"
        />
      </div>

      <div className="glass-table-card">
        {query.isError ? (
          <div className="approval-empty text-danger">
            <i className="bi bi-exclamation-triangle"></i>
            {getApiErrorMessage(query.error, "Could not load client visits.")}
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table mb-0 att-table">
                <thead className="custom-table-header">
                  <tr>
                    <th className="ps-4 py-3">Employee</th>
                    <th className="py-3">Visit date</th>
                    <th className="py-3">Clients</th>
                    <th className="py-3">Purpose</th>
                    <th className="py-3">Status</th>
                    <th className="py-3">Filed</th>
                    <th className="py-3 pe-4 text-end">Actions</th>
                  </tr>
                </thead>
                <tbody className="border-top-0">
                  {rows.map((v) => {
                    const meta = STATUSES.find((s) => s.value === v.status);
                    return (
                      <tr key={v.id}>
                        <td className="ps-4">
                          <div className="emp-name-text">
                            {v.employee?.name || "—"}
                          </div>
                          <div className="emp-sub-text">
                            {v.employee?.employeeNumber}
                            {v.employee?.branchName
                              ? ` · ${v.employee.branchName}`
                              : ""}
                          </div>
                        </td>
                        <td className="text-nowrap fw-semibold">
                          {formatPlainDate(v.date, "ddd, MMM D, YYYY")}
                        </td>
                        <td>
                          {(v.clients || []).map((c) => (
                            <div key={c.id}>
                              {c.name}
                              {!c.hasLocation && (
                                <i
                                  className="bi bi-geo-alt text-warning ms-1"
                                  title={NO_LOCATION}
                                  aria-label={NO_LOCATION}
                                ></i>
                              )}
                            </div>
                          ))}
                        </td>
                        <td>
                          <div className="approval-reason" title={v.purpose}>
                            {v.purpose || "—"}
                          </div>
                          {v.reviewRemarks && (
                            <div className="fs-075 text-slate-500 fst-italic">
                              “{v.reviewRemarks}”
                            </div>
                          )}
                        </td>
                        <td>
                          {v.expired ? (
                            <span className="status-pill status-pill--gray">
                              Not approved in time
                            </span>
                          ) : (
                            <span
                              className={`status-pill status-pill--${meta?.tone || "gray"}`}
                            >
                              {meta?.label || v.status}
                            </span>
                          )}
                          <ApprovalTrail approvals={v.approvals || EMPTY} />
                        </td>
                        <td
                          className="text-nowrap"
                          title={formatDateTime(v.createdAt)}
                        >
                          {v.createdAt ? fromNow(v.createdAt) : "—"}
                        </td>
                        <td className="pe-4 text-end text-nowrap">
                          {canModify && v.canReview && (
                            <div className="d-inline-flex gap-1">
                              <button
                                type="button"
                                className="btn btn-sm btn-success att-action-btn"
                                onClick={() =>
                                  setReviewing({
                                    visit: v,
                                    action: "APPROVE",
                                  })
                                }
                              >
                                Approve
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger att-action-btn"
                                onClick={() =>
                                  setReviewing({ visit: v, action: "REJECT" })
                                }
                              >
                                Reject
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {!query.isLoading && rows.length === 0 && (
                    <tr>
                      <td colSpan={7} className="approval-empty">
                        <i className="bi bi-briefcase"></i>No client visits
                        here.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {query.isLoading && (
              <div className="text-center py-5">
                <span
                  className="spinner-border text-primary"
                  role="status"
                  aria-label="Loading"
                ></span>
              </div>
            )}
            <Pagination
              page={page}
              limit={limit}
              total={query.data?.total || 0}
              onPageChange={setPage}
              onLimitChange={(l) => {
                setLimit(l);
                setPage(1);
              }}
            />
          </>
        )}
      </div>

      {reviewing && (
        <ReviewDialog
          visit={reviewing.visit}
          action={reviewing.action}
          onClose={() => setReviewing(null)}
        />
      )}
    </>
  );
};

export default ClientVisitQueue;
